"use client"

import { useCallback, useEffect, useState } from "react"
import { useApi } from "@/lib/api"
import { coalesceWorkflowFetch } from "@/hooks/workflow-fetch-cache"
import type { StageConfiguration, WorkflowConfiguration } from "@/types/workflow"

/**
 * Configuration descriptor for the ClientRequest tab.
 *
 * Mirrors {@link useVmsConfig}'s single-sub-tab shape but for ClientRequest's
 * one-stage layout. Per the lazy default-view materialization on the backend
 * (see WorkflowHubV2Service.GetWorkflowHubConfigurationsAsync), tenants that
 * have never visited Client Requests get a sensible default view built from
 * the seeded FieldConfigurations on first read — so the hook itself doesn't
 * need a separate "bootstrap" call.
 */
export interface ClientRequestConfig {
  entityType: "ClientRequest"
  stage: StageConfiguration | null
  configuration: WorkflowConfiguration | null
  /**
   * Total record count for the tab, fetched up-front so the tab pill can
   * show "Client Requests (n)" without waiting for the inner grid to load.
   * `null` while the count call is in-flight or if it failed (in which case
   * the UI suppresses the parenthesised number rather than showing a
   * confusing zero).
   */
  count: number | null
}

/**
 * Hook to fetch ClientRequest stage configuration + total row count.
 *
 * Configuration: GET /api/workflow/v2/configurations?entityType=ClientRequest,
 * coalesced with the inner WorkflowProvider's mount-time fetch so both
 * consumers share a single round-trip.
 *
 * Count: lightweight POST /api/workflow/v2/search with top=0, count=true to get
 * @odata.count without materialising rows. Same shape the VMS hook uses; the
 * backend search dispatcher already handles it, so no new endpoint.
 */
export function useClientRequestConfig() {
  const { workflowApi } = useApi()
  const [config, setConfig] = useState<ClientRequestConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const entityType: ClientRequestConfig["entityType"] = "ClientRequest"
      const params = new URLSearchParams({ entityType })
      const configUrl = `/api/workflow/v2/configurations?${params.toString()}`

      const [resolvedConfig, count] = await Promise.all([
        // Coalesce with the inner WorkflowProvider's mount-time fetch — both
        // hit the same URL within the same tick on initial paint. The shared
        // resolved value is already sorted by display order for whichever
        // consumer reads it first.
        coalesceWorkflowFetch(configUrl, async () => {
          const c = await workflowApi.get<WorkflowConfiguration>(configUrl)
          if (c && Array.isArray(c.stageConfigurations)) {
            c.stageConfigurations.forEach((stageConfig) => {
              if (Array.isArray(stageConfig.fieldConfigurations)) {
                stageConfig.fieldConfigurations.sort((a, b) => a.order - b.order)
              }
            })
            c.stageConfigurations.sort((a, b) => a.order - b.order)
          }
          return c
        }),
        // top=0 → backend skips materialising rows and just returns @odata.count.
        // Tolerate failure (null) so the tab still renders if the index isn't
        // ready yet.
        workflowApi
          .post<any>("/api/workflow/v2/search", {
            entityType,
            top: 0,
            skip: 0,
            search: "*",
            count: true,
            // Backend expects comma-separated $select; matches API_SELECT_FIELDS.join(',')
            // in use-workflow-api.ts.
            select: "id",
          })
          .then((r: any) => {
            const c = r?.["@odata.count"] ?? r?.count ?? r?.totalCount
            return typeof c === "number" ? c : null
          })
          .catch(() => null),
      ])

      setConfig({
        entityType,
        stage: resolvedConfig?.stageConfigurations?.[0] ?? null,
        configuration: resolvedConfig ?? null,
        count,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch ClientRequest configuration"
      setError(msg)
      setConfig(null)
    } finally {
      setIsLoading(false)
    }
  }, [workflowApi])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return {
    config,
    isLoading,
    error,
    refetch: fetchConfig,
  }
}
